import { supabase } from '../../../integrations/supabase/client';

export type Stage = {
  id: string;
  name: string;
  color: string | null;
  position: number;
  is_default?: boolean | null;
  user_id: string;
  created_at?: string | null;
  updated_at?: string | null;
};

export const stagesApi = {
  // Obtener todas las etapas del usuario
  async fetchStages(): Promise<Stage[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('funnel_stages')
        .select('*')
        .eq('user_id', user.id)
        .order('position', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('[stagesApi] Error fetching stages:', error);
      return [];
    }
  },

  // Crear una nueva etapa
  async createStage(name: string, color: string = '#6366F1'): Promise<Stage> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Obtener la posición más alta actual
      const { data: maxPositionData } = await supabase
        .from('funnel_stages')
        .select('position')
        .eq('user_id', user.id)
        .order('position', { ascending: false })
        .limit(1)
        .single();

      const newPosition = (maxPositionData?.position || 0) + 1;

      const { data, error } = await supabase
        .from('funnel_stages')
        .insert({
          user_id: user.id,
          name,
          color,
          position: newPosition,
          is_default: false,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[stagesApi] Error creating stage:', error);
      throw error;
    }
  },

  // Actualizar una etapa
  async updateStage(
    stageId: string,
    updates: Partial<Pick<Stage, 'name' | 'color' | 'position'>>
  ): Promise<Stage> {
    try {
      const { data, error } = await supabase
        .from('funnel_stages')
        .update(updates)
        .eq('id', stageId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('[stagesApi] Error updating stage:', error);
      throw error;
    }
  },

  // Eliminar una etapa (solo si no es default)
  async deleteStage(stageId: string): Promise<void> {
    try {
      // Verificar que no sea una etapa por defecto
      const { data: stage } = await supabase
        .from('funnel_stages')
        .select('is_default')
        .eq('id', stageId)
        .single();

      if (stage?.is_default) {
        throw new Error('No puedes eliminar la etapa por defecto');
      }

      const { error } = await supabase
        .from('funnel_stages')
        .delete()
        .eq('id', stageId);

      if (error) throw error;
    } catch (error) {
      console.error('[stagesApi] Error deleting stage:', error);
      throw error;
    }
  },

  // Asegurar que existen las etapas por defecto
  async ensureDefaultStage(): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const defaultStages = [
        { name: 'Nuevo', color: '#6366F1', position: 1, is_default: true },
        { name: 'En Conversación', color: '#3B82F6', position: 2, is_default: false },
        { name: 'Interesado', color: '#F59E0B', position: 3, is_default: false },
        { name: 'Concretado', color: '#10B981', position: 4, is_default: false },
        { name: 'Humano', color: '#EF4444', position: 5, is_default: false },
      ];

      // Remove legacy "Contacto 1" stage if it exists
      await supabase
        .from('funnel_stages')
        .delete()
        .eq('user_id', user.id)
        .eq('name', 'Contacto 1');

      const { data: existing } = await supabase
        .from('funnel_stages')
        .select('name')
        .eq('user_id', user.id);

      const existingNames = new Set((existing || []).map((s: any) => s.name));

      const toInsert = defaultStages
        .filter(s => !existingNames.has(s.name))
        .map(s => ({ ...s, user_id: user.id }));

      if (toInsert.length > 0) {
        await supabase.from('funnel_stages').insert(toInsert);
      }
    } catch (error) {
      console.error('[stagesApi] Error ensuring default stages:', error);
    }
  },
};
